import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ProxyJob } from '../../data/mockProxyJobs';
import { PROXY_SEED_TODAY } from '../../data/mockProxyJobs';
import { PROXY_WORKFLOW_REGISTRY, PROXY_SOURCE_LABEL, escalationFor, type ProxyTask } from '../../data/proxyWorkflowRegistry';
import { EscalateControl } from './EscalateControl';
import { useEmailInbox } from '../../contexts/EmailInboxContext';
import { resolveProxyJob } from '../../utils/proxyQueue';
import { BandChip } from './ProxyChips';
import { SearchInput } from '../SearchInput';
import { SortableTh } from '../SortableTh';
import { useSort, sortRows } from '../../utils/useSort';

// Workflow label comes straight from the registry entry — a new workflow
// type needs zero changes here to show up correctly.
function workflowLabel(type: ProxyTask['workflowType']): string {
  return PROXY_WORKFLOW_REGISTRY[type]?.label ?? type;
}

// task.createdAt is sometimes a plain ISO date and sometimes "YYYY-MM-DD HH:MM"
// (see materialFollowupWorkflow) — slice to the date before formatting.
// "Today"/"Yesterday" are relative to PROXY_SEED_TODAY, the demo's fixed
// narrative date, not the real calendar date.
function fmtDate(createdAt: string): string {
  const iso = createdAt.slice(0, 10);
  const today = PROXY_SEED_TODAY;
  const yest = new Date(new Date(today + 'T00:00:00').getTime() - 86400000).toISOString().split('T')[0];
  if (iso === today) return 'Today';
  if (iso === yest) return 'Yesterday';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export interface ProxyTaskRow {
  task: ProxyTask;
  job: ProxyJob;
}

interface Props {
  rows: ProxyTaskRow[];
  emptyTitle: string;
  emptyBody: string;
  // Hidden on the Escalations page itself — mirrors Fulfillment's
  // ExceptionQueueTable, which never shows an Escalate action on the page
  // that's already showing escalated rows.
  showEscalate?: boolean;
  onEscalate?: (jobId: string, reason: string) => void;
  // Escalations page: lets the manager hand a person-escalated task back.
  onSendBack?: (jobId: string) => void;
  // Set from a `?taskId=` deep link (e.g. the Email Inbox's "Related Task"
  // column) — scrolls that row into view and highlights it on arrival.
  highlightTaskId?: string | null;
  // Keeps this workflow type's rows above everything else, regardless of the
  // active sort column — for a page presenting one workflow front and center
  // (e.g. a demo walkthrough) without hiding the rest of the queue.
  pinWorkflowType?: ProxyTask['workflowType'];
}

export function ProxyTaskTable({ rows, emptyTitle, emptyBody, showEscalate, onEscalate, onSendBack, highlightTaskId, pinWorkflowType }: Props) {
  const navigate = useNavigate();
  const { proxyEmails } = useEmailInbox();
  const emailById = useMemo(() => new Map(proxyEmails.map(e => [e.id, e])), [proxyEmails]);
  const escalations = useMemo(() => new Map(rows.map(r => [r.task.id, escalationFor(r.task, r.job, proxyEmails)])), [rows, proxyEmails]);
  const [search, setSearch] = useState('');
  const { sort, toggle } = useSort({ column: 'createdAt', direction: 'desc' });
  const rowRefs = useRef(new Map<string, HTMLTableRowElement>());

  useEffect(() => {
    if (!highlightTaskId) return;
    rowRefs.current.get(highlightTaskId)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [highlightTaskId, rows]);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(r => r.job.issuer.toLowerCase().includes(q) || r.job.jobId.includes(q) || r.job.cusip.toLowerCase().includes(q));
  }, [rows, search]);

  const sorted = useMemo(() => {
    const base = sortRows(filtered, sort, (r, column) => {
      if (column === 'issuer') return r.job.issuer;
      if (column === 'workflow') return workflowLabel(r.task.workflowType);
      if (column === 'band') return resolveProxyJob(r.job, null).band;
      if (column === 'createdAt') return r.task.createdAt;
      return '';
    });
    if (!pinWorkflowType) return base;
    const pinned = base.filter(r => r.task.workflowType === pinWorkflowType);
    const rest = base.filter(r => r.task.workflowType !== pinWorkflowType);
    return [...pinned, ...rest];
  }, [filtered, sort, pinWorkflowType]);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs font-semibold whitespace-nowrap bg-wash border border-trim rounded-full px-3 py-1 text-copy">
          {rows.length} task{rows.length === 1 ? '' : 's'}
        </span>
        <SearchInput value={search} onChange={setSearch} placeholder="Search job #, issuer, CUSIP…" width={280} />
      </div>

      {sorted.length === 0 ? (
        <div className="px-4 py-10 rounded-xl border border-trim bg-wash text-center">
          <div className="text-sm font-medium text-[#0F6E56] mb-0.5">{emptyTitle}</div>
          <div className="text-xs text-muted">{emptyBody}</div>
        </div>
      ) : (
        <table data-tour="task-table" className="w-full text-sm border-collapse">
          <thead>
            <tr>
              <SortableTh label="Received" column="createdAt" sort={sort} onSort={toggle} />
              <SortableTh label="Issuer / Job" column="issuer" sort={sort} onSort={toggle} />
              <SortableTh label="Workflow" column="workflow" sort={sort} onSort={toggle} />
              <SortableTh label="Band" column="band" sort={sort} onSort={toggle} />
              <SortableTh label="ACE's reason" sort={sort} onSort={toggle} />
              <SortableTh label="Source" sort={sort} onSort={toggle} />
              {(showEscalate || onSendBack) && <SortableTh label="" sort={sort} onSort={toggle} />}
            </tr>
          </thead>
          <tbody>
            {sorted.map(({ task, job }) => {
              const escalation = escalations.get(task.id);
              const sourceEmail = task.source === 'email' && task.sourceEmailId ? emailById.get(task.sourceEmailId) : undefined;
              return (
              <tr
                key={task.id}
                data-tour={`task-row-${task.id}`}
                ref={el => { if (el) rowRefs.current.set(task.id, el); else rowRefs.current.delete(task.id); }}
                onClick={() => navigate('/ace', { state: { proxyTask: task } })}
                className={`cursor-pointer hover:bg-tint border-b border-trim ${task.id === highlightTaskId ? 'bg-[#FFF7D6]' : ''}`}
              >
                <td className="px-2 py-2.5 text-xs text-muted whitespace-nowrap">{fmtDate(task.createdAt)}</td>
                <td className="px-2 py-2.5">
                  <div className="text-sm font-medium text-ink">{task.jobIds && task.jobIds.length > 1 ? task.title : job.issuer}</div>
                  <div className="text-xs text-muted">{task.jobIds && task.jobIds.length > 1 ? task.subtitle : `Job ${job.jobId}`}</div>
                </td>
                <td className="px-2 py-2.5 text-xs text-copy">{workflowLabel(task.workflowType)}</td>
                <td className="px-2 py-2.5"><BandChip band={resolveProxyJob(job, null).band} /></td>
                <td className="px-2 py-2.5 text-xs text-copy max-w-[320px]">
                  {task.reason}
                  {escalation && (
                    <div className="mt-1 text-[#6d28d9]">
                      {escalation.by === 'Ace'
                        ? <><b className="font-semibold">Ace sent this to the manager:</b> {escalation.reason}</>
                        : <><b className="font-semibold">Escalated to the manager</b>{escalation.reason && <>. Comment: {escalation.reason}</>}</>}
                    </div>
                  )}
                </td>
                <td className="px-2 py-2.5 text-xs text-muted whitespace-nowrap">
                  {sourceEmail ? (
                    <button
                      onClick={e => { e.stopPropagation(); navigate(`/email-inbox?emailId=${sourceEmail.id}`); }}
                      className="text-[#2563EB] hover:underline max-w-[200px] truncate inline-block align-bottom"
                      title={sourceEmail.subject}
                    >
                      ✉ {sourceEmail.subject}
                    </button>
                  ) : (
                    PROXY_SOURCE_LABEL[task.source]
                  )}
                </td>
                {(showEscalate || onSendBack) && (
                  <td className="px-2 py-2.5 text-right" onClick={e => e.stopPropagation()}>
                    {onSendBack ? (
                      escalation?.by === 'You'
                        ? <button onClick={() => onSendBack(job.jobId)} className="text-xs font-medium text-[#2563EB] whitespace-nowrap">Send back to team</button>
                        : <span className="text-xs text-muted whitespace-nowrap">Clears once approved</span>
                    ) : escalation ? (
                      <span className="text-xs text-muted whitespace-nowrap">With the manager</span>
                    ) : (
                      <EscalateControl onEscalate={reason => onEscalate?.(job.jobId, reason)} />
                    )}
                  </td>
                )}
              </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
